import { TwoColumnLayout } from '@components/layout';
import { CheckboxGroup, FormError, FormRow, Icon, OrderableList, Tooltip } from '@components/ui';
import { getTabularModelClass } from '@helpers';
import { questionnaireStore } from '@stores';
import { notionStore } from '@stores/notion.store';
import { DataCollectionInputs, DataCollectionOptions } from '@types';
import DOMPurify from 'dompurify';
import { observer } from 'mobx-react';
import TabularStep1Illustration from 'public/illustrations/data-collection-step-2.svg';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import styles from '../../Steps.module.scss';

export type TabularMLStep1Options = Pick<
  DataCollectionOptions,
  'protectedGroups' | 'performanceMetrics' | 'protectedGroups'
>;

export type TabularMLStep1Data = Pick<DataCollectionInputs, 'modelType' | 'performanceMetricRanked'>;

interface TabularMLStep1Props {
  options: TabularMLStep1Options;
}

export const TabularMLStep1 = observer(({ options }: TabularMLStep1Props) => {
  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<TabularMLStep1Data>();

  const { values } = notionStore;
  const { questionnaireData } = questionnaireStore;

  const [performanceMetricRanked] = watch(['performanceMetricRanked']);
  const modelType = getTabularModelClass(questionnaireData);
  const rankedMetricIds = (performanceMetricRanked ?? []) as number[];
  const rankedMetricIdsAsStrings = rankedMetricIds.map(id => String(id));

  return (
    <TwoColumnLayout
      left={
        <ol className={styles.list}>
          <div>
            {modelType && (
              <FormRow>
                <li className={styles.questionLabel}>
                  <div>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(values?.step1Question2Text),
                      }}
                    />
                    {values?.step1Question2Info && (
                      <Tooltip
                        content={
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(values?.step1Question2Info, { ADD_ATTR: ['target'] }),
                            }}
                          />
                        }
                      >
                        <Icon name="question-mark" />{' '}
                      </Tooltip>
                    )}
                  </div>
                </li>
                <div>
                  <CheckboxGroup
                    // if other is selected then return all performance metrics, if not then filter by class
                    options={options.performanceMetrics.filter(elem => elem.modelClass === modelType)}
                    defaultSelected={rankedMetricIdsAsStrings}
                    onSelect={labels => {
                      // validation
                      if (labels.length === 0) {
                        setError('performanceMetricRanked', {
                          message: 'Please select at least one option',
                          type: 'required',
                        });
                      } else {
                        clearErrors('performanceMetricRanked');
                      }

                      // add last checked group into the ranked list (on the last place)
                      if (labels.length > rankedMetricIds.length) {
                        const lastLabel = labels[labels.length - 1];
                        const lastId = Number(lastLabel);
                        setValue('performanceMetricRanked', [...rankedMetricIds, lastId]);
                      } else {
                        const selectedIds = new Set(labels.map(label => Number(label)));
                        setValue(
                          'performanceMetricRanked',
                          rankedMetricIds.filter(id => selectedIds.has(id)),
                        );
                      }
                    }}
                  />
                </div>
                <FormError error={errors.performanceMetricRanked?.message} />
              </FormRow>
            )}
            {!modelType && <FormError error="Unable to determine the selected dataset model class." />}
            {rankedMetricIds.length > 0 && modelType && (
              <FormRow>
                <div>
                  <p className={styles.sublabel}>
                    <div>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(values?.step1Question3Text),
                        }}
                      />
                      {values?.step1Question3Info && (
                        <Tooltip
                          content={
                            <div
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(values?.step1Question3Info, { ADD_ATTR: ['target'] }),
                              }}
                            />
                          }
                        >
                          <Icon name="question-mark" />{' '}
                        </Tooltip>
                      )}
                    </div>
                  </p>
                  <div>
                    <OrderableList
                      list={rankedMetricIds
                        ?.map(metricId => options.performanceMetrics.find(metric => metric.id === metricId))
                        ?.filter(elem => elem && elem.modelClass === modelType)}
                      onReorder={newRanking => {
                        setValue(
                          'performanceMetricRanked',
                          newRanking.map(group => Number(group.id)),
                        );
                      }}
                    />
                  </div>
                </div>
              </FormRow>
            )}
          </div>
        </ol>
      }
      right={<TabularStep1Illustration />}
    />
  );
});
